import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Add, Search, Clear, Close } from "@mui/icons-material";
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import ClientTable from "../components/ClientTable";
import ClientFormModal from "../components/ClientFormModal";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import ClientInfoModal from "../components/ClientInfoModal";
import Pagination from "../components/Pagination";
import { useClientsApi } from "../hooks/useClientsApi";
import { Client, PaginationMeta } from "../types/entities";
import { ClientFormData } from "../types/forms";

const ClientsPage: React.FC = () => {
  const { t } = useTranslation();
  const [clients, setClients] = useState<Client[]>([]);
  const [paginationMeta, setPaginationMeta] = useState<PaginationMeta | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [initialLoading, setInitialLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [canDelete, setCanDelete] = useState<boolean>(false);
  const [formData, setFormData] = useState<ClientFormData>({
    name: "",
    phone_number: "",
  });

  const {
    error,
    fetchClients,
    createClient: createClientApi,
    updateClient: updateClientApi,
    deleteClient: deleteClientApi,
  } = useClientsApi();

  const loadClients = async (page: number = currentPage, limit: number = itemsPerPage): Promise<void> => {
    try {
      // Ensure page and limit are numbers
      const pageNum = Number(page);
      const limitNum = Number(limit);
      const queryParams: any = { 
        page: pageNum, 
        limit: limitNum,
        appointments: true
      };
      
      // Add search parameter if search term exists
      if (searchTerm && searchTerm.trim()) {
        queryParams.search = searchTerm.trim();
      }

      console.log('Loading clients with params:', queryParams);
      const response = await fetchClients(queryParams);
      console.log('Clients response:', response);
      setClients(response.data);
      setPaginationMeta(response.meta);
    } catch (err) {
      console.error('Error loading clients:', err);
    }
  };

  const createClient = async (data?: ClientFormData | Partial<ClientFormData>): Promise<void> => {
    try {
      // Use provided data or fallback to form data
      const dataToCreate = data || formData;
      await createClientApi(dataToCreate as ClientFormData);
      await loadClients();
      setOpenCreateModal(false);
      resetForm();
      toast.success(t('clients.clientCreated'));
    } catch (err) {
    }
  };

  const updateClient = async (changedData?: Partial<ClientFormData>): Promise<void> => {
    if (!selectedClient) return;

    try {
      // Use changed data if provided, otherwise use all form data
      const dataToUpdate = changedData || formData;
      await updateClientApi(selectedClient.id, dataToUpdate);
      await loadClients();
      setOpenEditModal(false);
      setSelectedClient(null);
      resetForm();
      toast.success(t('clients.clientUpdated'));
    } catch (err) {
    }
  };

  const handleDeleteClick = (client: Client): void => {
    setSelectedClient(client);
    
    // Check if client has appointments
    const hasAppointments = client.appointments && client.appointments.length > 0;
    
    if (hasAppointments) {
      // Show error dialog directly
      setDeleteError(t('clients.cannotDeleteClientError', { clientName: client.name, appointmentCount: client.appointments?.length || 0 }));
    } else {
      // Show confirmation dialog
      setCanDelete(true);
      setOpenDeleteDialog(true);
    }
  };

  const deleteClient = async (): Promise<void> => {
    if (!selectedClient) return;

    try {
      await deleteClientApi(selectedClient.id);
      await loadClients();
      setOpenDeleteDialog(false);
      setSelectedClient(null);
      setCanDelete(false);
      toast.success(t('clients.clientDeleted'));
    } catch (err: any) {
      // This should rarely happen now since we pre-check
      if (err.message && err.message.includes('appointment(s)')) {
        setDeleteError(err.message);
        setOpenDeleteDialog(false);
      }
      // For other errors, they will be handled by the error state from the hook
    }
  };

  const resetForm = (): void => {
    setFormData({
      name: "",
      phone_number: "",
    });
  };

  const handleInputChange =
    (field: keyof ClientFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: field === 'notes' && value === '' ? undefined : value,
      }));
    };

  const handleEditClick = (client: Client): void => {
    setSelectedClient(client);
    setFormData({
      name: client.name,
      phone_number: client.phone_number || undefined,
      notes: client.notes || undefined,
    });
    setOpenEditModal(true);
  };


  const handleInfoClick = (client: Client): void => {
    setSelectedClient(client);
    setOpenInfoModal(true);
  };

  const handleClearSearch = (): void => {
    setSearchTerm("");
    setCurrentPage(1);
    loadClients(1, itemsPerPage);
  };

  const handlePageChange = (page: number): void => {
    setCurrentPage(page);
    loadClients(page, itemsPerPage);
  };

  const handleLimitChange = (limit: number): void => {
    setItemsPerPage(limit);
    setCurrentPage(1);
    loadClients(1, limit);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleModalClose = (): void => {
    setOpenCreateModal(false);
    setOpenEditModal(false);
    resetForm();
  };

  // Debounced search effect
  useEffect(() => {
    if (!initialLoading) {
      const timeoutId = setTimeout(() => {
        // Reset to page 1 when searching
        setCurrentPage(1);
        loadClients(1, itemsPerPage);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm, itemsPerPage, initialLoading]);

  useEffect(() => {
    const initializeClients = async () => {
      try {
        console.log('Initializing clients...');
        await loadClients();
        console.log('Initial clients loaded');
      } finally {
        setInitialLoading(false);
      }
    };

    initializeClients();
  }, []);

  if (initialLoading) {
    return (
      <Container>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ 
          mb: 3,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0,
          textAlign: isMobile ? 'center' : 'left'
        }}
      >
        <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
          {t('clients.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenCreateModal(true)}
          size={isMobile ? 'medium' : 'large'}
          fullWidth={isMobile}
        >
          {t('clients.addClient')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      {/* Always show search input */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder={t('clients.searchPlaceholder')}
          value={searchTerm}
          onChange={handleSearchChange}
          size={isMobile ? 'medium' : 'medium'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                >
                  <Clear />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Show results or no results message */}
      {clients.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm ? t('clients.noClientsMatching', { searchTerm }) : t('clients.noClientsFound')}
          </Typography>
          {!searchTerm && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {t('clients.noClientsMessage')}
            </Typography>
          )}
          {searchTerm && (
            <Button
              variant="outlined"
              onClick={handleClearSearch}
              startIcon={<Clear />}
              sx={{ mt: 2 }}
            >
              {t('clients.clearSearch')}
            </Button>
          )}
        </Box>
      ) : (
        <>
          <ClientTable
            clients={clients}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onInfo={handleInfoClick}
          />
          {paginationMeta && (
            <Box sx={{ mt: 3 }}>
              <Pagination
                meta={paginationMeta}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
              />
            </Box>
          )}
        </>
      )}

      <ClientFormModal
        open={openCreateModal}
        onClose={handleModalClose}
        onSubmit={createClient}
        formData={formData}
        onInputChange={handleInputChange}
        title={t('clients.addNewClient')}
        submitButtonText={t('common.create')}
      />

      <ClientFormModal
        open={openEditModal}
        onClose={handleModalClose}
        onSubmit={updateClient}
        formData={formData}
        onInputChange={handleInputChange}
        title={t('clients.editClient')}
        submitButtonText={t('common.save')}
        isEdit={true}
      />

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={deleteClient}
        client={selectedClient}
      />

      <ClientInfoModal
        open={openInfoModal}
        onClose={() => setOpenInfoModal(false)}
        client={selectedClient}
      />

      {/* Custom dialog for delete error with active appointments */}
      <Dialog open={!!deleteError} onClose={() => setDeleteError(null)}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('clients.deleteClient')}</Typography>
            <IconButton
              aria-label="close"
              onClick={() => setDeleteError(null)}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('clients.cannotDeleteClient')}
          </Alert>
          <Typography>
            <strong>{selectedClient?.name}</strong> {t('clients.cannotDeleteClientMessage', { 
              clientName: selectedClient?.name || '', 
              appointmentCount: selectedClient?.appointments?.length || 1,
              appointmentCountPlural: selectedClient?.appointments?.length !== 1 ? 'y' : ''
            })}
          </Typography>
          <Typography sx={{ mt: 1 }}>
            {t('clients.cannotDeleteClientInstructions')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteError(null)} variant="outlined">
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClientsPage;
